<template>
  <section class="min-w-0 w-full space-y-6">
    <h1 class="text-2xl font-semibold">Capacity Plan Import</h1>

    <div class="flex gap-6 border-b border-gray-200" role="tablist">
      <button role="tab" :aria-selected="activeTab === 'file'" :disabled="store.saving" :class="tabClass('file')" @click="activeTab = 'file'">Import from file</button>
      <button role="tab" :aria-selected="activeTab === 'manual'" :disabled="store.saving" :class="tabClass('manual')" @click="activeTab = 'manual'">Manual entry</button>
    </div>

    <v-progress-linear v-if="store.saving" indeterminate color="primary" />

    <ManualCapacityEntry v-if="activeTab === 'manual'" :capacity-types="capacityTypes" />

    <template v-if="activeTab === 'file'">
      <div class="card space-y-4">
        <CapacityPlanFileInput v-model="selectedFile" :max-bytes="MAX_UPLOAD_BYTES" :disabled="store.loading" />

        <div class="flex items-center gap-3">
          <button class="btn-primary" type="button" :disabled="!selectedFile || fileTooLarge || store.loading" @click="onParse">
            {{ store.loading ? 'Parsing…' : 'Parse file' }}
          </button>
          <p v-if="store.loading" class="m-0 text-sm text-gray-500">Parsing the workbook with AI — this usually takes up to a minute.</p>
        </div>
      </div>

      <ImportHistoryPanel v-if="store.records.length > 0" @load="store.loadRecord($event)" />

      <div v-if="store.error" class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {{ store.error }}
      </div>

      <template v-if="store.result">
        <div v-for="(warning, index) in store.result.warnings" :key="index" class="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {{ warning }}
        </div>

        <div class="card">
          <h2 class="text-lg font-medium mb-2">Vendor</h2>
          <template v-if="store.result.vendorMatch && store.result.vendorMatchConfidence === 'exact'">
            <p class="m-0">
              Matched:
              <strong>{{ store.result.vendorMatch.name }}</strong>
              <span v-if="store.result.vendorMatch.alias">({{ store.result.vendorMatch.alias }})</span>
            </p>
          </template>
          <template v-else-if="store.result.vendorMatch">
            <p class="m-0">
              Possible match:
              <strong>{{ store.result.vendorMatch.name }}</strong>
              <span v-if="store.result.vendorMatch.alias">({{ store.result.vendorMatch.alias }})</span>
              for
              <strong v-if="store.result.vendorName">"{{ store.result.vendorName }}"</strong>
              in the sheet.
            </p>
            <p class="mt-1 mb-0">
              Partial matches are not used for saving. If this is the right vendor, add
              <strong v-if="store.result.vendorName">"{{ store.result.vendorName }}"</strong>
              as its alias — otherwise import the correct vendor from Odoo. Lines with their own vendor code still save to their vendors.
            </p>
            <RouterLink class="btn-secondary mt-3" :to="{ name: 'VendorList' }">Go to vendors</RouterLink>
          </template>
          <template v-else>
            <p class="m-0">
              No portal vendor matched
              <strong v-if="store.result.vendorName">"{{ store.result.vendorName }}"</strong>
              . Lines whose vendor code matches a portal vendor still save to their vendors; import missing vendors from Odoo.
            </p>
            <RouterLink class="btn-secondary mt-3" :to="{ name: 'VendorList' }">Go to vendors</RouterLink>
          </template>
        </div>

        <div class="card space-y-3">
          <h2 class="text-lg font-medium m-0">Save to capacity calendar</h2>
          <div v-if="store.conflictingFactorCodes.length > 0" class="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Conflicting capacity factors for {{ store.conflictingFactorCodes.join(', ') }} — fix the factors below before saving.
          </div>
          <div v-if="store.totalExistingOverlap > 0 && !store.confirmResult" class="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            {{ store.totalExistingOverlap }} week bucket(s) in this plan are already planned — they are kept as-is unless you tick "overwrite" on those weeks below.
          </div>
          <div v-if="store.plannedPoTotal > 0 && !store.confirmResult" class="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {{ store.plannedPoTotal }} planned PO(s) sit inside already-planned weeks. Overwriting those weeks replaces the capacity underneath the planned POs — the POs stay
            assigned, but the weeks may become overbooked.
          </div>
          <div v-if="diffStrip.length > 0 && !store.confirmResult" class="flex flex-wrap gap-2">
            <span v-for="chip in diffStrip" :key="chip.label" class="rounded-full px-3 py-1 text-xs font-medium" :class="chip.class">
              {{ chip.label }}
            </span>
          </div>
          <div v-if="!store.hasExistingData && !store.confirmResult" class="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
            Couldn't load existing capacity for comparison — old-vs-new differences aren't shown for this import.
          </div>
          <template v-if="!store.confirmResult">
            <label v-if="store.overbookedWeeks.length > 0" class="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              <input v-model="store.overbookAcknowledged" type="checkbox" class="mt-0.5 h-4 w-4 rounded border-red-300 text-red-600 focus:ring-red-500" />
              I understand {{ store.overbookedWeeks.length }} week(s) will become overbooked — the assigned POs exceed the new capacity.
            </label>
            <button class="btn-primary" type="button" :disabled="store.saving || !store.canSave" @click="store.confirmImport()">
              {{ store.saving ? 'Saving…' : 'Save capacity allocations' }}
            </button>
            <p v-if="!store.canSave" class="m-0 text-sm text-gray-500">
              Saving needs at least one line with a known type, no conflicting factors, and a vendor selected on every line (lines marked "unknown vendor" need one picked from the
              list).
            </p>
          </template>
          <template v-else>
            <div class="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
              {{ confirmSummary }}
            </div>
            <div v-for="(warning, index) in store.confirmResult.warnings" :key="index" class="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              {{ warning }}
            </div>
          </template>
        </div>

        <div class="card space-y-3">
          <div>
            <h2 class="text-lg font-medium m-0">Weeks</h2>
            <p class="mt-1 mb-0 text-xs text-gray-500">
              Shared by every production line below. Fix the year for the whole plan in one edit, or pick a week's own date range if just that column is off.
            </p>
          </div>
          <label class="block w-24 space-y-1">
            <span class="label m-0">Year</span>
            <input
              type="number"
              :value="planYear"
              title="Replaces the year on every week (month/day unchanged), then re-snaps to Monday"
              class="input py-1 text-sm tabular-nums"
              @change="onYearChange"
            />
          </label>
          <div class="flex flex-wrap gap-3">
            <div v-for="(week, weekIndex) in store.result.weeks" :key="weekIndex" class="space-y-1">
              <div class="text-xs font-semibold text-gray-700">W{{ week.weekNumber }}</div>
              <v-menu :close-on-content-click="false">
                <template #activator="{ props: menuProps }">
                  <button type="button" class="input w-40 py-1 text-left text-xs tabular-nums hover:border-gray-300" v-bind="menuProps">
                    {{ week.startDate }} – {{ week.endDate }}
                  </button>
                </template>
                <VDateRangePicker :model-value="weekRangeModel(week)" @update:model-value="onWeekRangeChange(weekIndex, $event)" />
              </v-menu>
            </div>
          </div>
        </div>

        <div v-if="store.uncoveredPlannedWeeks.length > 0" class="card">
          <button type="button" class="flex w-full items-center justify-between text-left" @click="uncoveredOpen = !uncoveredOpen">
            <span class="text-lg font-medium">{{ store.uncoveredPlannedWeeks.length }} planned week(s) not in this file</span>
            <span class="text-sm text-gray-500">{{ uncoveredOpen ? 'Hide' : 'Show' }}</span>
          </button>
          <p class="mt-1 mb-0 text-xs text-gray-500">Already planned in the portal but outside this file's weeks — this import will not touch them.</p>
          <table v-if="uncoveredOpen" class="mt-3 min-w-full text-sm">
            <thead class="bg-gray-50">
              <tr>
                <th class="table-th">Week start</th>
                <th class="table-th text-right">Planning capacity</th>
                <th class="table-th text-right">POs</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="week in store.uncoveredPlannedWeeks" :key="`${week.entryKey}:${week.weekStart}`">
                <td class="px-4 py-1.5 tabular-nums">{{ week.weekStart }}</td>
                <td class="px-4 py-1.5 text-right tabular-nums">{{ week.planningCapacity }}</td>
                <td class="px-4 py-1.5 text-right tabular-nums">{{ week.poCount }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <section class="space-y-4">
          <div class="flex items-baseline justify-between">
            <h2 class="text-lg font-medium">Production lines</h2>
            <p class="text-xs text-gray-500">Values are editable — click a cell to correct parsing errors before saving.</p>
          </div>

          <div v-for="(line, lineIndex) in store.result.productionLines" :key="line.lineLabel" class="card p-0 overflow-hidden" :class="{ 'opacity-50': line.skipped }">
            <div class="flex flex-wrap items-center gap-3 border-b border-gray-100 px-4 py-3">
              <label class="flex items-center gap-1.5 text-xs font-medium text-gray-500" title="Exclude this line entirely — it won't be saved">
                <input
                  type="checkbox"
                  class="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  :checked="line.skipped"
                  @change="store.toggleLineSkipped(lineIndex)"
                />
                Skip
              </label>
              <span class="text-sm font-semibold text-gray-900">{{ line.lineLabel }}</span>

              <select :value="line.vendorId ?? ''" class="select w-auto py-1 text-xs font-medium" @change="onLineVendorChange(lineIndex, $event)">
                <option value="" disabled>{{ line.vendorCode ?? 'vendor' }} — select vendor</option>
                <option v-for="vendor in lineVendorOptions(line.vendorId)" :key="vendor.id" :value="vendor.id">
                  {{ vendor.name }}
                  <template v-if="vendor.alias">({{ vendor.alias }})</template>
                  <template v-if="isArchived(vendor)">(archived)</template>
                </option>
              </select>
              <span v-if="!line.vendorId" class="badge bg-amber-100 text-amber-800">unknown vendor</span>

              <select :value="line.terminationTypeId ?? ''" class="select w-auto py-1 text-xs font-medium" @change="onTerminationTypeChange(lineIndex, $event)">
                <option value="" disabled>{{ line.terminationTypeCode }} — select type</option>
                <option v-for="type in capacityTypes" :key="type.id" :value="type.id">{{ type.code }} — {{ type.label }}</option>
              </select>

              <span v-if="!line.terminationTypeId" class="badge bg-amber-100 text-amber-800">unknown type</span>
              <span v-else-if="store.conflictingFactorCodes.includes(line.terminationTypeCode)" class="badge bg-red-100 text-red-800">conflicting factor</span>
              <span v-if="(store.existingWeekCounts[lineIndex] ?? 0) > 0" class="badge bg-blue-50 text-blue-700">
                {{ store.existingWeekCounts[lineIndex] }} week(s) already planned
              </span>

              <label class="ml-auto flex items-center gap-2 text-xs text-gray-500">
                Capacity factor
                <input
                  type="number"
                  step="0.05"
                  min="0"
                  max="1"
                  :value="line.capacityFactor ?? ''"
                  placeholder="—"
                  class="input w-20 py-1 text-right tabular-nums"
                  @change="onCapacityFactorChange(lineIndex, $event)"
                />
              </label>
            </div>

            <div class="overflow-x-auto">
              <table class="min-w-full text-sm">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="table-th sticky left-0 z-10 bg-gray-50">Metric</th>
                    <th v-for="week in store.result.weeks" :key="week.weekNumber" class="whitespace-nowrap px-3 py-2 text-right align-top">
                      <div class="text-xs font-semibold text-gray-700">W{{ week.weekNumber }}</div>
                      <div class="text-[11px] font-normal text-gray-400">{{ week.startDate }} – {{ week.endDate }}</div>
                      <label
                        v-if="store.weekAlreadyPlanned(lineIndex, week.startDate)"
                        class="mt-1 flex items-center justify-end gap-1 text-[11px] font-medium normal-case tracking-normal text-amber-700"
                        :title="
                          store.plannedPoCount(lineIndex, week.startDate) > 0
                            ? `${store.plannedPoCount(lineIndex, week.startDate)} planned PO(s) in this week — overwriting replaces the capacity underneath them`
                            : 'This week already has an allocation — tick to overwrite it'
                        "
                      >
                        <input
                          type="checkbox"
                          class="h-3 w-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          :checked="store.isOverwriteSelected(lineIndex, week.startDate)"
                          @change="store.toggleOverwriteWeek(lineIndex, week.startDate)"
                        />
                        already planned — overwrite
                        <span v-if="store.plannedPoCount(lineIndex, week.startDate) > 0" class="font-semibold">({{ store.plannedPoCount(lineIndex, week.startDate) }} PO)</span>
                      </label>
                      <div v-if="overbookedBy(lineIndex, week.startDate) > 0" class="mt-1 text-right">
                        <span class="badge bg-red-100 text-red-800">overbooked by {{ overbookedBy(lineIndex, week.startDate) }}</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100">
                  <tr v-for="(metric, metricIndex) in line.metrics" :key="metric.name">
                    <td class="sticky left-0 z-10 whitespace-nowrap bg-white px-4 py-1.5 font-medium text-gray-700">
                      {{ metric.name }}
                    </td>
                    <td v-for="(value, weekIndex) in metric.values" :key="weekIndex" class="px-1.5 py-1 text-right">
                      <input
                        type="number"
                        :value="value ?? ''"
                        placeholder="—"
                        class="w-24 rounded-md border border-transparent bg-transparent px-2 py-1 text-right tabular-nums text-gray-900 hover:border-gray-300 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                        @change="onMetricValueChange(lineIndex, metricIndex, weekIndex, $event)"
                      />
                      <div
                        v-if="weekStartAt(weekIndex) !== null && cellDiffText(lineIndex, weekStartAt(weekIndex) as string, metric.key)"
                        class="pr-2 text-[11px]"
                        :class="cellDiffClass(lineIndex, weekStartAt(weekIndex) as string)"
                      >
                        {{ cellDiffText(lineIndex, weekStartAt(weekIndex) as string, metric.key) }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </template>
    </template>
  </section>
</template>

<script setup lang="ts">
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
  import { useRoute } from 'vue-router';
  import { VDateRangePicker } from 'vuetify/labs/VDateRangePicker';
  import CapacityPlanFileInput from '@/components/capacity/CapacityPlanFileInput.vue';
  import ImportHistoryPanel from '@/components/capacity/ImportHistoryPanel.vue';
  import { useAccess } from '@/composables/useAccess';
  import { fetchCapacityTypes } from '@/services/capacityTypeService';
  import type { BucketDiff } from '@/store/capacityImportStore';
  import { useCapacityImportStore } from '@/store/capacityImportStore';
  import { useVendorStore } from '@/store/vendorStore';
  import { PERMISSIONS } from '@/types/access';
  import type { CapacityMetricKey, CapacityPlanWeek } from '@/types/capacityImport';
  import type { CapacityType } from '@/types/capacityType';
  import type { VendorSummary } from '@/types/vendor';
  import { parseNullableNumber } from '@/utils/numberInput';
import { isArchived, vendorPickerOptions } from '@/utils/vendorArchive';
  import ManualCapacityEntry from './ManualCapacityEntry.vue';

  const MAX_UPLOAD_MB = 10;
  const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

  const store = useCapacityImportStore();
  const vendorStore = useVendorStore();
  const route = useRoute();
  const { hasAllVendors, hasPermission, scopedVendors } = useAccess();
  const selectedFile = ref<File | null>(null);
  const capacityTypes = ref<CapacityType[]>([]);
  const activeTab = ref<'file' | 'manual'>('file');

  // All-vendor pickers source the active vendor directory; scoped callers can't list every
  // vendor, so theirs source from the vendors scoped to their account on /me instead.
  // The backend keeps a line's vendorId on an archived vendor (warns, then skips it on confirm); keep it selectable-looking.
  function lineVendorOptions(selectedId: string | null): (VendorSummary & { archivedAt?: string | null })[] {
    return hasAllVendors.value ? vendorPickerOptions(vendorStore.activeVendors, vendorStore.vendors, selectedId) : scopedVendors.value;
  }

  // A partner scoped to exactly one vendor has no real choice to make — preselect it so a
  // line doesn't sit on the disabled placeholder (and block saving as "unknown vendor").
  watch(
    () => store.result,
    (result) => {
      if (hasAllVendors.value || scopedVendors.value.length !== 1 || !result) return;
      const [onlyVendor] = scopedVendors.value;
      result.productionLines.forEach((line, lineIndex) => {
        if (!line.vendorId) store.updateLineVendor(lineIndex, onlyVendor.id);
      });
    }
  );

  function tabClass(tab: 'file' | 'manual'): string {
    return activeTab.value === tab
      ? 'border-b-2 border-blue-600 px-1 pb-2 text-sm font-semibold text-blue-700'
      : 'border-b-2 border-transparent px-1 pb-2 text-sm font-medium text-gray-500 hover:text-gray-700';
  }

  const fileTooLarge = computed(() => (selectedFile.value?.size ?? 0) > MAX_UPLOAD_BYTES);

  const planYear = computed(() => {
    const firstWeek = store.result?.weeks[0];
    return firstWeek ? Number(firstWeek.startDate.slice(0, 'YYYY'.length)) : null;
  });

  const confirmSummary = computed(() => {
    if (!store.confirmResult) return '';
    return store.confirmResult.vendors
      .map((vendor) => {
        const factors = vendor.settingsSaved > 0 ? `, ${vendor.settingsSaved} factor(s) saved` : '';
        const kept = vendor.allocationsSkipped > 0 ? `, ${vendor.allocationsSkipped} kept (already planned)` : '';
        return `${vendor.vendorName}: ${vendor.allocationsCreated} allocation(s) created, ${vendor.allocationsUpdated} updated${kept}${factors}`;
      })
      .join(' · ');
  });

  async function onParse(): Promise<void> {
    if (!selectedFile.value || fileTooLarge.value) return;
    await store.parse(selectedFile.value);
    await store.loadRecords();
  }

  function parseNullableNumberFromInput(event: Event): number | null {
    return parseNullableNumber((event.target as HTMLInputElement).value);
  }

  function onMetricValueChange(lineIndex: number, metricIndex: number, weekIndex: number, event: Event): void {
    store.updateMetricValue(lineIndex, metricIndex, weekIndex, parseNullableNumberFromInput(event));
  }

  function onCapacityFactorChange(lineIndex: number, event: Event): void {
    store.updateCapacityFactor(lineIndex, parseNullableNumberFromInput(event));
  }

  function onYearChange(event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    if (!Number.isInteger(value)) return;
    store.updateWeeksYear(value);
  }

  // Vuetify's date adapter works in local time; convert at this boundary rather than
  // reusing the store's UTC-based ISO math, to avoid an off-by-one-day shift.
  function isoToLocalDate(iso: string): Date {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  function localDateToIso(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  const MS_PER_DAY = 86_400_000;

  // VDateRangePicker's model is every date in the range, not just the two endpoints.
  function weekRangeModel(week: CapacityPlanWeek): Date[] {
    const start = isoToLocalDate(week.startDate);
    const end = isoToLocalDate(week.endDate);
    const dayCount = Math.round((Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()) - Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())) / MS_PER_DAY) + 1;
    return Array.from({ length: Math.max(dayCount, 0) }, (_, offset) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + offset));
  }

  function onWeekRangeChange(weekIndex: number, dates: unknown): void {
    if (!Array.isArray(dates) || dates.length === 0) return;
    const isoDates = (dates as Date[]).map(localDateToIso).sort();
    store.updateWeekRange(weekIndex, isoDates[0], isoDates[isoDates.length - 1]);
  }

  function onTerminationTypeChange(lineIndex: number, event: Event): void {
    const typeId = (event.target as HTMLSelectElement).value;
    const type = capacityTypes.value.find((candidate) => candidate.id === typeId);
    if (type) store.updateTerminationType(lineIndex, type);
  }

  function onLineVendorChange(lineIndex: number, event: Event): void {
    const vendorId = (event.target as HTMLSelectElement).value;
    if (vendorId) store.updateLineVendor(lineIndex, vendorId);
  }

  const numberFormat = new Intl.NumberFormat();

  const diffStrip = computed(() => {
    const summary = store.diffSummary;
    if (!summary) return [];
    return [
      { label: `↑ ${summary.increased} increased`, show: summary.increased > 0, class: 'bg-green-50 text-green-700' },
      { label: `↓ ${summary.decreased} decreased`, show: summary.decreased > 0, class: 'bg-amber-50 text-amber-700' },
      { label: `+ ${summary.created} new`, show: summary.created > 0, class: 'bg-blue-50 text-blue-700' },
      { label: `${summary.changed} changed`, show: summary.changed > 0, class: 'bg-amber-50 text-amber-700' },
      { label: `${summary.unchanged} unchanged`, show: summary.unchanged > 0, class: 'bg-gray-100 text-gray-600' },
      { label: `${summary.skipped} kept (no overwrite)`, show: summary.skipped > 0, class: 'bg-gray-100 text-gray-600' },
      { label: `⚠ ${summary.overbooked} overbooked`, show: summary.overbooked > 0, class: 'bg-red-50 text-red-700' },
      {
        label: `${store.uncoveredPlannedWeeks.length} planned week(s) not in this file`,
        show: store.uncoveredPlannedWeeks.length > 0,
        class: 'bg-gray-100 text-gray-600',
      },
    ].filter((chip) => chip.show);
  });

  // Which existing value a metric row diffs against.
  function beforeValueFor(diff: BucketDiff, key: CapacityMetricKey): number | null {
    if (!diff.before) return null;
    if (key === 'capacity') return diff.before.capacity ?? null;
    if (key === 'lineQty') return diff.before.lineQty ?? null;
    if (key === 'input') return diff.before.vendorInput ?? null;
    if (key === 'remainedCapacity') return diff.before.vendorRemained ?? null;
    return null;
  }

  // metric.values can be longer than store.result.weeks on AI-parsed sheets — an out-of-range
  // weekIndex must not fall back to '' (that flows into bucketDiffFor -> mondayOf('') and throws).
  function weekStartAt(weekIndex: number): string | null {
    return store.result?.weeks[weekIndex]?.startDate ?? null;
  }

  function cellDiffText(lineIndex: number, weekStart: string, key: CapacityMetricKey): string | null {
    const diff = store.bucketDiffFor(lineIndex, weekStart);
    if (!diff) return null;
    if (diff.status === 'new') return key === 'capacity' ? 'new' : null;
    const before = beforeValueFor(diff, key);
    if (before === null) return null;
    const percent =
      key === 'capacity' && diff.planningDeltaPercent !== null && diff.planningDeltaPercent !== 0
        ? ` (${diff.planningDeltaPercent > 0 ? '+' : ''}${diff.planningDeltaPercent}%)`
        : '';
    return `was ${numberFormat.format(before)}${percent}`;
  }

  function cellDiffClass(lineIndex: number, weekStart: string): string {
    const diff = store.bucketDiffFor(lineIndex, weekStart);
    if (!diff) return 'text-gray-400';
    if (diff.status === 'new') return 'text-blue-600';
    if (diff.status === 'increased') return 'text-green-600';
    if (diff.status === 'decreased' || diff.status === 'changed') return 'text-amber-600';
    return 'text-gray-400';
  }

  function overbookedBy(lineIndex: number, weekStart: string): number {
    return store.bucketDiffFor(lineIndex, weekStart)?.overbookedBy ?? 0;
  }

  const uncoveredOpen = ref(false);

  onMounted(async () => {
    // Only all-vendor callers need the full directory (scoped ones use /me, see lineVendorOptions).
    // GET /vendors needs read:vendors, which access:all-vendors is expected to be paired with.
    if (hasAllVendors.value && hasPermission(PERMISSIONS.READ_VENDORS)) void vendorStore.fetchVendors();
    await store.loadRecords();
    try {
      capacityTypes.value = await fetchCapacityTypes();
    } catch {
      // The select degrades to showing the parsed code only; unknown-type editing is unavailable.
      capacityTypes.value = [];
    }

    // Hand-off from the calendar's "Open full import" bar action: reload that parse record's
    // preview instead of starting from an empty page (store.loadRecord already handles
    // vendor rebinding/normalization for a stored preview — see capacityImportStore.ts).
    const recordId = route.query.record;
    if (typeof recordId === 'string') {
      await store.loadRecord(recordId);
    }
  });

  onUnmounted(() => {
    store.reset();
  });
</script>
